<template>
  <div class="lg:w-3/4">
    <div class="primary font-semibold text-2xl mb-2">
      Review Changes
    </div>

    <Error
      v-if="error"
      :error="error"
      class="mb-6"
    />

    <p
      v-else
      class="mb-6"
    >
      Please review your changes. All updates are final and cannot be undone.
    </p>

    <div class="flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <h4 class="text-lg font-semibold">
          Updated Payment Schedule
        </h4>
      </div>

      <Table
        :table-id="'review'"
        item-type="Payment"
        :items="editingSchedule"
        :headers="paymentsTableHeaders"
      >
        <template #row(dueDate)="{ item }">
          <div
            class="py-2"
            :class="{ 'font-semibold': isDateChanged(item.installmentId) }"
          >
            {{ item.formattedDueDate }}
          </div>
        </template>

        <template #row(amount)="{ item }">
          <span :class="{ 'font-semibold': isAmountChanged(item.installmentId) }">
            {{ formatCurrencyCents(item.currency, item.amountInCents) }}
          </span>
        </template>

        <template #row(status)="{ item }">
          <PaymentStatus :formattedStatus="item.formattedStatus" />
        </template>
      </Table>
    </div>
    <div class="mt-4 flex justify-between items-center">
      <div class="text-gray-500">
        {{ '' /* This filtering removes summary that backend returns that we thought was confusing for users */ }}
        {{ summary.filter(x => !x.match(/Changed the order/)).join(', ') }}
      </div>
      <div class="flex gap-2 items-center whitespace-nowrap">
        <router-link
          class="btn btn-ghost btn-sm"
          :to="backLink"
        >
          Back
        </router-link>
        <button
          class="btn btn-primary"
          :disabled="submitting || !!error"
          @click="confirmChanges"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { formatDate } from '@/filters/date';
import { formatCurrencyCents } from '@/filters/currency';
import Table from '@/components/table.vue';
import { EditingPayment, ResultChangedInstallments } from '../types';
import PaymentStatus from './payment_status.vue';
import Error from './error.vue';

interface Props {
  summary: string[];
  submitting: boolean;
  editingSchedule: EditingPayment[];
  changeResult: ResultChangedInstallments
  previewError: unknown
  updateError: unknown
}

const error = computed(() => props.previewError || props.updateError);

const paymentsTableHeaders = [
  {
    title: 'Date (UTC)',
    key: 'dueDate',
    formatter: (val: string) => formatDate(val, 'MMM dd, yyyy'),
    maxWidth: '80px',
  },
  {
    title: 'Amount',
    key: 'amount',
    class: 'currency',
  },
  {
    title: 'Status',
    key: 'status',
  },
];

const props = defineProps<Props>();
const emit = defineEmits(['back', 'confirm']);

const isDateChanged = (installmentId: string): boolean => (!!props.changeResult[installmentId]?.dueDate);

const isAmountChanged = (installmentId: string): boolean => (!!props.changeResult[installmentId]?.amountInCents);

const isRemoved = (installmentId: string): boolean => props.changeResult[installmentId]?.amountInCents?.newAmountInCents === 0;

const confirmChanges = () => {
  emit('confirm');
};

const editingSchedule = computed(() => props.editingSchedule.map((int) => {
  let rowClass = '';

  if (!int.isEditable) {
    rowClass = 'bg-gray-100';
  }

  if (isAmountChanged(int.installmentId) || isDateChanged(int.installmentId)) {
    rowClass = 'bg-[#80C05B26]';
  } else if (!int.isEditable) {
    rowClass = 'bg-gray-100';
  } else {
    rowClass = 'bg-white';
  }

  if (isRemoved(int.installmentId)) {
    rowClass += ' opacity-50 line-through';
  }
  const copy = {
    ...int,
    rowClass,
  };
  return copy;
}));

const route = useRoute();

const backLink = computed(() => {
  const { path, query } = route;
  const { review: _, ...newQuery } = query;
  return {
    path,
    query: newQuery,
  };
});

</script>
